import React, { useState, useEffect } from 'react';
import { PasswordInput } from '@mantine/core';
import { resetPassword } from '../../services/auth';
import { validatePassword, getPasswordRequirementStatus } from '../../utils/passwordValidation';
import styles from './passwordReset.module.css';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner.jsx';
import { useNavigate } from 'react-router-dom';

export default function PasswordReset() {
  const [passwordOne, setPasswordOne] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [buttonDisable, setButtonDisable] = useState(true);
  const [message, setMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [requirementStatus, setRequirementStatus] = useState({
    minLength: false,
    noConsecutive: false,
    noInvalidChars: true,
  });
  const [loading, setLoading] = useState(false);
  const [successFlag, setSuccessFlag] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'KNOW - Reset Password';
  }, []);

  const handlePasswordOne = ({ target }) => {
    setPasswordOne(target.value);
  };

  const handlePasswordTwo = ({ target }) => {
    setPasswordTwo(target.value);
  };

  useEffect(() => {
    if (successFlag) {
      const redirectTimeout = setTimeout(() => {
        return navigate('/login', { replace: true });
      }, 4000);
      return () => clearTimeout(redirectTimeout);
    }
  }, [successFlag]);

  useEffect(() => {
    if (passwordOne === '') {
      setPasswordError('');
      setButtonDisable(true);
      setRequirementStatus({ minLength: false, noConsecutive: false, noInvalidChars: true });
      return;
    }

    const debounceTimer = setTimeout(() => {
      setRequirementStatus(getPasswordRequirementStatus(passwordOne));

      const validation = validatePassword(passwordOne);
      if (!validation.valid) {
        setPasswordError('');
        setButtonDisable(true);
        return;
      }

      if (passwordOne !== passwordTwo) {
        setPasswordError(passwordTwo ? 'Passwords do not match.' : '');
        setButtonDisable(true);
        return;
      }

      setPasswordError('');
      setButtonDisable(false);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [passwordOne, passwordTwo]);

  const handleChangePassword = () => {
    setLoading(true);
    const token = new URL(document.location).searchParams.get('token');
    resetPassword(token, passwordOne)
      .then(() => {
        setMessage('Success! You will be redirected to the login page in a few seconds.');
        setSuccessFlag(true);
        setLoading(false);
      })
      .catch((err) => {
        if (err.status === 401 || err.message === 'JWT must be provided.') {
          setMessage('Please go back to your email to follow the password reset link once more.');
        } else {
          setMessage(err.message);
        }
        setLoading(false);
      });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className={styles.resetContainer}>
      <div className={styles.forgot}>
        <h1>Reset Your Password</h1>
        {message && (
          <p className={styles.message} style={{ textAlign: 'center', 'padding-bottom': '1rem' }}>
            {message}
          </p>
        )}
        {!successFlag && (
          <div style={{ display: 'flex', 'flex-direction': 'column' }}>
            <section>
              <ul>
                {' '}
                <strong>Your new password must:</strong>
                <li>Be different than your old password</li>
                <li
                  className={
                    requirementStatus.minLength ? styles.requirementMet : styles.requirementNotMet
                  }
                >
                  {requirementStatus.minLength ? '✓' : '○'} Be at least 12 characters long
                </li>
                <li
                  className={
                    requirementStatus.noConsecutive
                      ? styles.requirementMet
                      : styles.requirementNotMet
                  }
                >
                  {requirementStatus.noConsecutive ? '✓' : '○'} Not contain more than 3 consecutive
                  identical characters
                </li>
              </ul>
            </section>
            <PasswordInput
              label="Please enter your new password:"
              value={passwordOne}
              onChange={handlePasswordOne}
            />
            <PasswordInput
              label="Please confirm your new password:"
              value={passwordTwo}
              onChange={handlePasswordTwo}
              error={passwordError}
            />
            <button
              className={styles.submitButton}
              disabled={buttonDisable}
              onClick={handleChangePassword}
            >
              Change your password
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
